/**
 * useLongPress — pure timer logic for touch long-press drag capture.
 *
 * On touch pointers (pointerType 'touch'), a drag should only start after
 * a ~300ms hold. This prevents scroll hijacking when a user tries to scroll
 * the board on mobile (core-flows §3.2 phone drag: "long-press (400ms) on
 * the drag handle").
 *
 * Mouse and pen pointers are unaffected — they start dragging immediately
 * on pointerdown (no hold required).
 *
 * This module exports PURE timer logic (no React, no DOM) so it can be
 * tested without a browser environment. The hook wrapping is thin and
 * delegates to these pure functions.
 *
 * Immutability: all functions are pure; timer IDs are returned for caller
 * to manage (never stored in module state).
 */

export const TOUCH_LONG_PRESS_MS = 300;

/**
 * Returns true if the pointer event represents a touch pointer.
 * Only 'touch' requires the long-press delay; 'mouse' and 'pen' do not.
 *
 * Pure function — no side effects.
 */
export function isTouchPointer(pointerType: string): boolean {
  return pointerType === 'touch';
}

/**
 * Compute the delay in milliseconds before a drag should start for this
 * pointer type.
 *
 *   touch  → TOUCH_LONG_PRESS_MS (300ms)
 *   mouse  → 0 (immediate)
 *   pen    → 0 (immediate)
 *
 * Pure function.
 */
export function dragStartDelayMs(pointerType: string): number {
  return isTouchPointer(pointerType) ? TOUCH_LONG_PRESS_MS : 0;
}

/**
 * Schedule `callback` after the appropriate delay for `pointerType`.
 * Returns a timer ID (number) that the caller must cancel on pointerup /
 * pointercancel / pointermove-beyond-slop to abort the long-press.
 *
 * If the delay is 0, the callback is invoked synchronously and the returned
 * ID is -1 (no timer to cancel).
 *
 * Side effect: may call setTimeout. Caller owns the returned ID.
 */
export function scheduleDragStart(
  pointerType: string,
  callback: () => void,
): number {
  const delay = dragStartDelayMs(pointerType);
  if (delay === 0) {
    callback();
    return -1; // no pending timer
  }
  return window.setTimeout(callback, delay);
}

/**
 * Cancel a scheduled drag-start timer. Safe to call with -1 (no-op).
 */
export function cancelDragStart(timerId: number): void {
  if (timerId !== -1) {
    clearTimeout(timerId);
  }
}
